import React, { useState, useEffect, useRef } from 'react';
import { Clock, Play, Pause, RotateCcw, Timer, Coffee, Zap, Heart } from 'lucide-react';
import { PageTracker } from '../../components/PageTracker.tsx';

interface TimerState {
  minutes: number;
  seconds: number;
  isRunning: boolean;
  isBreak: boolean;
  session: number;
}

const TimerHub: React.FC = () => {
  const [activeTimer, setActiveTimer] = useState('pomodoro');
  const [pomodoroState, setPomodoroState] = useState<TimerState>({
    minutes: 25,
    seconds: 0,
    isRunning: false,
    isBreak: false,
    session: 1
  });
  
  const [customTimer, setCustomTimer] = useState({
    minutes: 10,
    seconds: 0,
    isRunning: false,
    totalMinutes: 10,
    totalSeconds: 0
  });

  const [stopwatch, setStopwatch] = useState({
    minutes: 0,
    seconds: 0,
    centiseconds: 0,
    isRunning: false
  });

  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const timers = [
    { id: 'pomodoro', name: 'Pomodoro Timer', icon: Timer, color: 'bg-red-500' },
    { id: 'custom', name: 'Custom Timer', icon: Clock, color: 'bg-blue-500' },
    { id: 'stopwatch', name: 'Stopwatch', icon: Zap, color: 'bg-green-500' }
  ];

  useEffect(() => {
    if (activeTimer === 'pomodoro' && pomodoroState.isRunning) {
      intervalRef.current = setInterval(() => {
        setPomodoroState(prev => {
          if (prev.seconds > 0) {
            return { ...prev, seconds: prev.seconds - 1 };
          } else if (prev.minutes > 0) {
            return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
          } else {
            // Timer finished
            const newIsBreak = !prev.isBreak;
            const newSession = prev.isBreak ? prev.session + 1 : prev.session;
            return {
              ...prev,
              minutes: newIsBreak ? 5 : 25,
              seconds: 0,
              isBreak: newIsBreak,
              session: newSession,
              isRunning: false
            };
          }
        });
      }, 1000);
    } else if (activeTimer === 'custom' && customTimer.isRunning) {
      intervalRef.current = setInterval(() => {
        setCustomTimer(prev => {
          if (prev.seconds > 0) {
            return { ...prev, seconds: prev.seconds - 1 };
          } else if (prev.minutes > 0) {
            return { ...prev, minutes: prev.minutes - 1, seconds: 59 };
          } else {
            return { ...prev, isRunning: false };
          }
        });
      }, 1000);
    } else if (activeTimer === 'stopwatch' && stopwatch.isRunning) {
      intervalRef.current = setInterval(() => {
        setStopwatch(prev => {
          let newCentiseconds = prev.centiseconds + 1;
          let newSeconds = prev.seconds;
          let newMinutes = prev.minutes;

          if (newCentiseconds >= 100) {
            newCentiseconds = 0;
            newSeconds += 1;
          }
          if (newSeconds >= 60) {
            newSeconds = 0;
            newMinutes += 1;
          }

          return {
            ...prev,
            centiseconds: newCentiseconds,
            seconds: newSeconds,
            minutes: newMinutes
          };
        });
      }, 10);
    }

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
    };
  }, [activeTimer, pomodoroState.isRunning, customTimer.isRunning, stopwatch.isRunning]);

  const formatTime = (minutes: number, seconds: number) => {
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const formatStopwatchTime = (minutes: number, seconds: number, centiseconds: number) => {
    return `${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}.${centiseconds.toString().padStart(2, '0')}`;
  };

  const togglePomodoroTimer = () => {
    setPomodoroState(prev => ({ ...prev, isRunning: !prev.isRunning }));
  };

  const resetPomodoroTimer = () => {
    setPomodoroState({
      minutes: 25,
      seconds: 0,
      isRunning: false,
      isBreak: false,
      session: 1
    });
  };

  const toggleCustomTimer = () => {
    setCustomTimer(prev => ({ ...prev, isRunning: !prev.isRunning }));
  };

  const resetCustomTimer = () => {
    setCustomTimer(prev => ({
      ...prev,
      minutes: prev.totalMinutes,
      seconds: prev.totalSeconds,
      isRunning: false
    }));
  };

  const setCustomTimerTime = (minutes: number, seconds: number) => {
    setCustomTimer(prev => ({
      ...prev,
      minutes,
      seconds,
      totalMinutes: minutes,
      totalSeconds: seconds,
      isRunning: false
    }));
  };

  const toggleStopwatch = () => {
    setStopwatch(prev => ({ ...prev, isRunning: !prev.isRunning }));
  };

  const resetStopwatch = () => {
    setStopwatch({
      minutes: 0,
      seconds: 0,
      centiseconds: 0,
      isRunning: false
    });
  };

  const renderTimerContent = () => {
    switch (activeTimer) {
      case 'pomodoro':
        return (
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <div className="flex items-center justify-center space-x-2 mb-4">
                {pomodoroState.isBreak ? (
                  <>
                    <Coffee className="w-6 h-6 text-green-500" />
                    <span className="text-lg font-medium text-green-600">Break Time</span>
                  </>
                ) : (
                  <>
                    <Heart className="w-6 h-6 text-red-500" />
                    <span className="text-lg font-medium text-red-600">Focus Time</span>
                  </>
                )}
              </div>
              
              <div className="text-8xl font-mono font-bold text-gray-900 mb-4">
                {formatTime(pomodoroState.minutes, pomodoroState.seconds)}
              </div>
              
              <div className="text-lg text-gray-600 mb-6">
                Session {pomodoroState.session}
              </div>
            </div>

            <div className="flex justify-center space-x-4">
              <button
                onClick={togglePomodoroTimer}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                  pomodoroState.isRunning
                    ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                    : 'bg-red-500 hover:bg-red-600 text-white'
                }`}
              >
                {pomodoroState.isRunning ? (
                  <>
                    <Pause className="w-5 h-5" />
                    <span>Pause</span>
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    <span>Start</span>
                  </>
                )}
              </button>
              
              <button
                onClick={resetPomodoroTimer}
                className="flex items-center space-x-2 px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
              >
                <RotateCcw className="w-5 h-5" />
                <span>Reset</span>
              </button>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">How Pomodoro Works:</h4>
              <ul className="text-sm text-gray-600 space-y-1">
                <li>• 25 minutes of focused work</li>
                <li>• 5 minutes break</li>
                <li>• Repeat for better productivity</li>
              </ul>
            </div>
          </div>
        );

      case 'custom':
        return (
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <div className="text-8xl font-mono font-bold text-gray-900 mb-4">
                {formatTime(customTimer.minutes, customTimer.seconds)}
              </div>
              
              <div className="flex justify-center space-x-4 mb-6">
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Minutes</label>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={customTimer.totalMinutes}
                    onChange={(e) => setCustomTimerTime(parseInt(e.target.value) || 0, customTimer.totalSeconds)}
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500"
                    disabled={customTimer.isRunning}
                  />
                </div>
                <div className="space-y-2">
                  <label className="block text-sm font-medium text-gray-700">Seconds</label>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={customTimer.totalSeconds}
                    onChange={(e) => setCustomTimerTime(customTimer.totalMinutes, parseInt(e.target.value) || 0)}
                    className="w-20 px-3 py-2 border border-gray-300 rounded-lg text-center focus:ring-2 focus:ring-blue-500"
                    disabled={customTimer.isRunning}
                  />
                </div>
              </div>
            </div>

            <div className="flex justify-center space-x-4">
              <button
                onClick={toggleCustomTimer}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                  customTimer.isRunning
                    ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                    : 'bg-blue-500 hover:bg-blue-600 text-white'
                }`}
              >
                {customTimer.isRunning ? (
                  <>
                    <Pause className="w-5 h-5" />
                    <span>Pause</span>
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    <span>Start</span>
                  </>
                )}
              </button>
              
              <button
                onClick={resetCustomTimer}
                className="flex items-center space-x-2 px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
              >
                <RotateCcw className="w-5 h-5" />
                <span>Reset</span>
              </button>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <button
                onClick={() => setCustomTimerTime(5, 0)}
                className="p-3 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-lg font-medium transition-colors"
                disabled={customTimer.isRunning}
              >
                5 Minutes
              </button>
              <button
                onClick={() => setCustomTimerTime(15, 0)}
                className="p-3 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-lg font-medium transition-colors"
                disabled={customTimer.isRunning}
              >
                15 Minutes
              </button>
              <button
                onClick={() => setCustomTimerTime(30, 0)}
                className="p-3 bg-blue-100 hover:bg-blue-200 text-blue-800 rounded-lg font-medium transition-colors"
                disabled={customTimer.isRunning}
              >
                30 Minutes
              </button>
            </div>
          </div>
        );

      case 'stopwatch':
        return (
          <div className="text-center space-y-8">
            <div className="space-y-4">
              <div className="text-8xl font-mono font-bold text-gray-900 mb-4">
                {formatStopwatchTime(stopwatch.minutes, stopwatch.seconds, stopwatch.centiseconds)}
              </div>
              
              <div className="text-lg text-gray-600">
                Elapsed Time
              </div>
            </div>

            <div className="flex justify-center space-x-4">
              <button
                onClick={toggleStopwatch}
                className={`flex items-center space-x-2 px-6 py-3 rounded-lg font-medium transition-colors ${
                  stopwatch.isRunning
                    ? 'bg-yellow-500 hover:bg-yellow-600 text-white'
                    : 'bg-green-500 hover:bg-green-600 text-white'
                }`}
              >
                {stopwatch.isRunning ? (
                  <>
                    <Pause className="w-5 h-5" />
                    <span>Stop</span>
                  </>
                ) : (
                  <>
                    <Play className="w-5 h-5" />
                    <span>Start</span>
                  </>
                )}
              </button>
              
              <button
                onClick={resetStopwatch}
                className="flex items-center space-x-2 px-6 py-3 bg-gray-500 hover:bg-gray-600 text-white rounded-lg font-medium transition-colors"
              >
                <RotateCcw className="w-5 h-5" />
                <span>Reset</span>
              </button>
            </div>

            <div className="bg-gray-50 p-4 rounded-lg">
              <h4 className="font-semibold text-gray-900 mb-2">Precision Timing</h4>
              <p className="text-sm text-gray-600">
                Accurate to centiseconds for precise time measurement
              </p>
            </div>
          </div>
        );

      default:
        return (
          <div className="text-center py-12">
            <Clock className="w-16 h-16 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900 mb-2">Select a Timer</h3>
            <p className="text-gray-500">Choose from Pomodoro, Custom Timer, or Stopwatch</p>
          </div>
        );
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-white to-blue-50 p-6">
      <PageTracker pageTitle="Timer Hub" category="tools" />
      <div className="max-w-6xl mx-auto">
        {/* Header */}
        <div className="mb-8">
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Timer Hub</h1>
          <p className="text-gray-600 text-lg">
            Productivity timers, custom countdowns, and precision stopwatch
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          {/* Timer Selection */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Timers</h3>
              <div className="space-y-2">
                {timers.map((timer) => {
                  const Icon = timer.icon;
                  return (
                    <button
                      key={timer.id}
                      onClick={() => setActiveTimer(timer.id)}
                      className={`w-full flex items-center space-x-3 p-3 rounded-lg transition-colors ${
                        activeTimer === timer.id
                          ? `${timer.color} text-white`
                          : 'hover:bg-gray-50 text-gray-700'
                      }`}
                    >
                      <Icon className="w-5 h-5" />
                      <span className="font-medium">{timer.name}</span>
                    </button>
                  );
                })}
              </div>
            </div>
          </div>

          {/* Timer Content */}
          <div className="lg:col-span-3">
            <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-8">
              {renderTimerContent()}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default TimerHub;
